import React, { createContext, Dispatch, RefObject, PropsWithChildren, use, useMemo, useReducer, useRef } from 'react';
import { Subject } from 'rxjs';
import { BadgeProps } from '@components-clean/core/Badge';

export type ToolbarComponents =
    | 'popOut'
    | 'legendToggle'
    | 'commentsToggle'
    | 'subjectMonitoringProfileOverviewToggle'
    | 'actionButton'
    | 'riskReviewActions';

export type ToolbarComponentsWithPermission = {
    component: ToolbarComponents;
    permissionElement?: string;
};

type ActionTypes =
    | 'setComponents'
    | 'setPopoutRoute'
    | 'setActionButton'
    | 'setRiskReviewActions'
    | 'setSubjectMonitoringProfileOverviewToggle'
    | 'setToolbarBadges'
    | 'setCommentsToggle'
    | 'showCommentsToggle'
    | 'hideCommentsToggle';

interface PageToolbarActions extends Partial<PageToolbarState> {
    type: ActionTypes;
}

interface IActionButton {
    text: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    style?: React.CSSProperties;
}

interface IRiskReviewActions {
    content?: React.ReactNode;
}

type ToolbarBadgeProps = BadgeProps & {
    key: React.Key;
};

export interface PageToolbarState {
    components: ToolbarComponents[];
    legendToggle?: boolean | null;
    commentsToggle?: boolean | null;
    subjectMonitoringProfileOverviewToggle?: boolean | null;
    popoutRoute?: string;
    riskReviewActions?: IRiskReviewActions;
    actionButton?: IActionButton;
    toolbarBadges?: ToolbarBadgeProps[] | null;
}

export interface IPageToolbarContext {
    state: PageToolbarState;
    dispatch: Dispatch<PageToolbarActions>;
    legendToggle$: Subject<boolean>;
    legendToggleValue: RefObject<boolean> | null;
}

const defaultState: PageToolbarState = {
    components: [],
};

export const PageToolbarContext = createContext<IPageToolbarContext>({
    state: defaultState,
    dispatch: () => {},
    legendToggle$: new Subject(),
    legendToggleValue: null,
});

const legendToggle$ = new Subject<boolean>();

const reducer = (state: PageToolbarState, action: PageToolbarActions): PageToolbarState => {
    switch (action.type) {
        case 'setComponents':
            // Default the legend toggle value to false if the component is present
            let legendToggle = null;
            let subjectMonitoringProfileOverviewToggle = null;
            if (action.components?.includes('legendToggle')) legendToggle = false;
            if (action.components?.includes('subjectMonitoringProfileOverviewToggle')) {
                subjectMonitoringProfileOverviewToggle = true;
            }

            return {
                ...state,
                components: action.components ?? [],
                legendToggle,
                subjectMonitoringProfileOverviewToggle,
                popoutRoute: undefined,
            };
        case 'setPopoutRoute':
            return { ...state, popoutRoute: action.popoutRoute };
        case 'setActionButton':
            return { ...state, actionButton: action.actionButton };
        case 'setRiskReviewActions':
            return { ...state, riskReviewActions: action.riskReviewActions };
        case 'setSubjectMonitoringProfileOverviewToggle':
            return { ...state, subjectMonitoringProfileOverviewToggle: action.subjectMonitoringProfileOverviewToggle };
        case 'setToolbarBadges':
            return { ...state, toolbarBadges: action.toolbarBadges };
        case 'setCommentsToggle':
            return { ...state, commentsToggle: action.commentsToggle };
        case 'showCommentsToggle':
            return {
                ...state,
                components: [...state.components.filter((c) => c !== 'commentsToggle'), 'commentsToggle'],
            };
        case 'hideCommentsToggle':
            return {
                ...state,
                components: state.components.filter((c) => c !== 'commentsToggle'),
            };
        default:
            throw new Error();
    }
};

export const PageToolbarContextProvider = ({ children }: PropsWithChildren) => {
    const [state, dispatch] = useReducer(reducer, defaultState);
    const legendToggleValueRef = useRef<boolean>(false);

    return (
        <PageToolbarContext
            value={useMemo(
                () => ({
                    state,
                    dispatch,
                    legendToggle$,
                    legendToggleValue: legendToggleValueRef,
                }),
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/exhaustive-deps
                [state, dispatch, legendToggle$, legendToggleValueRef],
            )}
        >
            {children}
        </PageToolbarContext>
    );
};

export const usePageToolbar = () => use(PageToolbarContext);
